<template>
  <div class="offer-content-double">
    <!-- Description Text -->
    <div
      class="offer-description"
      :style="descriptionStyle"
      v-html="formattedDescription"
    ></div>

    <!-- First Package (Middle) -->
    <div v-if="firstPackage" class="package-container package-first">
      <!-- More Content Badge -->
      <OfferMoreContent
        :package="firstPackage"
        :display-config="firstPackageDisplayConfig"
        :offer-display-config="displayConfig"
        :custom-style="firstMoreContentStyle"
      />

      <!-- Old Price Badge -->
      <OfferOriginalPrice
        :package="firstPackage"
        :display-config="firstPackageDisplayConfig"
        :custom-style="firstOriginalPriceStyle"
      />

      <!-- Package Assets Display -->
      <OfferPackageAssets
        :package="firstPackage"
        :custom-style="firstAssetsStyle"
      />

      <!-- CTA Button -->
      <button class="offer-cta-button" @click="handlePurchase(firstPackage)">
        {{ getCtaButtonText(firstPackage) }}
      </button>
    </div>

    <!-- Second Package (Right) -->
    <div v-if="secondPackage" class="package-container package-second">
      <!-- More Content Badge -->
      <OfferMoreContent
        :package="secondPackage"
        :display-config="secondPackageDisplayConfig"
        :offer-display-config="displayConfig"
        :custom-style="secondMoreContentStyle"
      />

      <!-- Old Price Badge -->
      <OfferOriginalPrice
        :package="secondPackage"
        :display-config="secondPackageDisplayConfig"
        :custom-style="secondOriginalPriceStyle"
      />

      <!-- Package Assets Display -->
      <OfferPackageAssets
        :package="secondPackage"
        :custom-style="secondAssetsStyle"
      />

      <!-- CTA Button -->
      <button class="offer-cta-button" @click="handlePurchase(secondPackage)">
        {{ getCtaButtonText(secondPackage) }}
      </button>
    </div>
  </div>
</template>

<script>
import { offerMethodsMixin } from "./offerMethodsMixin";
import OfferMoreContent from "./offer-more-content.vue";
import OfferOriginalPrice from "./offer-original-price.vue";
import OfferPackageAssets from "./offer-package-assets.vue";

export default {
  name: "OfferLayoutDouble",
  components: {
    OfferMoreContent,
    OfferOriginalPrice,
    OfferPackageAssets,
  },
  mixins: [offerMethodsMixin],
  props: {
    offer: {
      type: Object,
      required: true,
    },
    displayConfig: {
      type: Object,
      default: null,
    },
    formattedDescription: {
      type: String,
      default: "",
    },
    descriptionStyle: {
      type: Object,
      default: () => ({}),
    },
  },
  computed: {
    firstPackage() {
      return this.offer?.packages?.[0] || null;
    },
    secondPackage() {
      return this.offer?.packages?.[1] || null;
    },
    firstPackageDisplayConfig() {
      if (!this.firstPackage?.display_config) return null;
      try {
        return typeof this.firstPackage.display_config === "string"
          ? JSON.parse(this.firstPackage.display_config)
          : this.firstPackage.display_config;
      } catch (e) {
        console.error("Error parsing first package display_config:", e);
        return null;
      }
    },
    secondPackageDisplayConfig() {
      if (!this.secondPackage?.display_config) return null;
      try {
        return typeof this.secondPackage.display_config === "string"
          ? JSON.parse(this.secondPackage.display_config)
          : this.secondPackage.display_config;
      } catch (e) {
        console.error("Error parsing second package display_config:", e);
        return null;
      }
    },
    firstMoreContentStyle() {
      return {
        top: "82px",
        right: "594px",
        width: "160px",
        height: "160px",
        padding: "20px",
        fontSize: "20px",
      };
    },
    firstOriginalPriceStyle() {
      return {
        width: "110px",
        height: "43px",
        top: "528px",
        left: "986px",
        padding: "10px",
        fontSize: "28px",
        lineHeight: "25px",
      };
    },
    firstAssetsStyle() {
      return {
        top: "142px",
        left: "692px",
        height: "367px",
        width: "332px",
      };
    },
    secondMoreContentStyle() {
      return {
        top: "153px",
        right: "277px",
        width: "110px",
        height: "110px",
        padding: "20px",
        fontSize: "20px",
      };
    },
    secondOriginalPriceStyle() {
      return {
        width: "86px",
        height: "32px",
        top: "538px",
        right: "286px",
        padding: "5px",
        fontSize: "23px",
        lineHeight: "23px",
      };
    },
    secondAssetsStyle() {
      return {
        top: "206px",
        left: "1098px",
        height: "303px",
        width: "273px",
      };
    },
  },
};
</script>

<style scoped lang="scss">
@import "./offer-shared-styles";

// Layout-specific positioning and sizing for double package layout

.offer-description {
  left: 245px;
  top: 315px;
  max-width: 400px;
}

.package-container {
  position: relative;
}

// First Package (Middle) - 100% base size
.package-first {
  .offer-cta-button {
    left: 692px;
    top: 550px;
    width: 329px;
    height: 102px;
    font-size: 35px;
    line-height: 35px;
  }

  // More content badge text sizing
  ::v-deep .offer-more-content {
    .more-content-number {
      font-size: 45px;
    }

    .more-content-text {
      font-size: 20px;
      margin-top: 5px;
    }
  }

  // Asset sizing
  ::v-deep .offer-assets {
    .asset-row-other {
      gap: 10px;
    }

    .asset-item-main {
      .asset-icon {
        width: 140px;
        height: 140px;
      }

      .asset-amount {
        font-size: 42px;
      }
    }

    .asset-item-other {
      .asset-icon {
        width: 70px;
        height: 70px;
      }

      .asset-amount {
        font-size: 22px;
      }
    }
  }
}

// Second Package (Right) - 80% of base size
.package-second {
  .offer-cta-button {
    left: 1100px;
    top: 559px;
    width: 268px;
    height: 90px;
    font-size: 26px;
    line-height: 26px;
  }

  // More content badge text sizing
  ::v-deep .offer-more-content {
    .more-content-number {
      font-size: 30px;
    }

    .more-content-text {
      font-size: 15px;
      margin-top: 5px;
    }
  }

  // Asset sizing
  ::v-deep .offer-assets {
    .asset-row-other {
      gap: 10px;
    }

    .asset-item-main {
      .asset-icon {
        width: 112px;
        height: 112px;
      }

      .asset-amount {
        font-size: 33.6px;
      }
    }

    .asset-item-other {
      .asset-icon {
        width: 56px;
        height: 56px;
      }

      .asset-amount {
        font-size: 17.6px;
      }
    }
  }
}
</style>
